import Pusher from 'pusher-js';
export interface JobUpdateEvent {
  job: {
    id: string;
    name: string;
    queue: string;
  };
  status: "waiting" | "progress" | "completed" | "error";
  data?: any;
  progress?: {
    total: number;
    completed: number;
  };
}
export default class OnvoBase {
  #apiKey: string;
  _endpoint: string;

  // Base fetch method
  /**
   * Fetches data from the API.
   * @param url - The URL to fetch data from.
   * @param method - The HTTP method to use. Defaults to "GET".
   * @param body - The data to send with the request.
   * @param isForm - Indicates if the request is a form request.
   * @returns The response from the API.
   * @throws {Error} If there is an error making the request.
   */
  async _fetchBase(
    url: string,
    method?: "GET" | "PUT" | "POST" | "DELETE" | "PATCH",
    body?: any,
    isForm?: boolean
  ): Promise<any> {
    let headers: any = {
      "Access-Control-Allow-Origin": "*",
      "Access-Control-Allow-Methods": "GET,PUT,POST,DELETE,PATCH,OPTIONS",
    };
    if (this.#apiKey && this.#apiKey.trim() !== "") {
      headers["x-api-key"] = this.#apiKey;
    }
    if (!isForm) {
      headers["Content-Type"] = "application/json";
    }
    const response = await fetch(this._endpoint + url, {
      method: method || "GET",
      headers: headers,
      body: isForm ? body : body ? JSON.stringify(body) : undefined,
    });

    if (!response.ok) {
      let data: any = await response.json();
      throw new Error(data.message);
    } else {
      let data = await response.json();
      return data;
    }
  }

  async _fetchStreamBase(url: string, method?: "GET" | "PUT" | "POST" | "DELETE" | "PATCH", body?: any, onProgress?: (event: JobUpdateEvent) => void) {
    // Always non-streaming HTTP. Long-lived updates are delivered over WebSockets using channel=jobId.
    let headers: any = {
      "Access-Control-Allow-Origin": "*",
      "Access-Control-Allow-Methods": "GET,PUT,POST,DELETE,PATCH,OPTIONS",
      "Content-Type": "application/json",
    };
    if (this.#apiKey && this.#apiKey.trim() !== "") {
      headers["x-api-key"] = this.#apiKey;
    }

    const response = await fetch(this._endpoint + url, {
      method: method || "POST",
      headers,
      body: body ? JSON.stringify(body) : undefined,
    });

    // Background mode: server returns 202 with { status: 'accepted', jobId/channel }
    if (response.status === 202) {
      const accepted = await response.json().catch(() => ({ status: 'accepted' }));
      const jobChannel: string | undefined = accepted?.channel || accepted?.jobId;
      const socketCfg = accepted?.socket as undefined | {
        key: string;
        wsHost: string;
        wsPort?: number;
        wssPort?: number;
        forceTLS?: boolean;
        cluster?: string;
      };

      // If we don't have channel or socket configuration, return the accepted payload
      if (!jobChannel || !socketCfg) {
        return accepted;
      }

      // Connect to Soketi/Pusher and stream events to onProgress; resolve on completed
      return await new Promise<any>((resolve, reject) => {
        try {
          const isLocal = socketCfg.wsHost.includes('localhost') || socketCfg.wsHost.includes('host.docker.internal');
          const [host, portStr] = socketCfg.wsHost.split(':');
          const port = portStr ? parseInt(portStr, 10) : socketCfg.wsPort;

          const pusherConfig = {
            wsHost: host,
            wsPort: port,
            wssPort: socketCfg.wssPort,
            forceTLS: isLocal ? false : !!socketCfg.forceTLS,
            disableStats: true,
            enabledTransports: isLocal ? ['ws'] : ['ws', 'wss'],
            cluster: socketCfg.cluster || 'mt1',
          };

          const pusher = new Pusher(socketCfg.key, pusherConfig as any);

          const channel = pusher.subscribe(jobChannel);
          let cleaned = false;
          let settled = false;

          const cleanup = () => {
            if (cleaned) return;
            cleaned = true;
            try { channel.unbind_all(); } catch { }
            try { pusher.connection.unbind('error'); } catch { }
            try { pusher.connection.unbind('state_change'); } catch { }
            try {
              const state = (pusher as any)?.connection?.state;
              if (state && state !== 'disconnected' && state !== 'failed') {
                pusher.disconnect();
              }
            } catch { }
          };
          const forward = (evt: JobUpdateEvent) => {
            if (typeof onProgress === 'function') {
              try { onProgress(evt); } catch { }
            }
          };

          let timer: any = null;
          const timeoutMs = 10000; // 60s
          timer = setTimeout(() => {
            console.error('[Onvo] WebSocket subscription timed out.');
            settleReject(new Error('WebSocket subscription timeout'));
          }, timeoutMs);

          const settleResolve = (val: any) => {
            if (settled) return; settled = true;
            try { resolve(val.data); } finally { setTimeout(() => { if (timer) try { clearTimeout(timer); } catch { } cleanup(); }, 0); }
          };
          const settleReject = (err: any) => {
            if (settled) return; settled = true;
            try { reject(err); } finally { setTimeout(() => { if (timer) try { clearTimeout(timer); } catch { } cleanup(); }, 0); }
          };

          channel.bind('pusher:subscription_succeeded', () => {
            if (timer) clearTimeout(timer); // Clear the timeout on successful subscription

            channel.bind('job.accepted', (data: any) => forward({ ...(data || {}), status: 'waiting' } as JobUpdateEvent));
            channel.bind('job.waiting', (data: any) => forward(data as JobUpdateEvent));
            channel.bind('job.progress', (data: any) => forward(data as JobUpdateEvent));
            channel.bind('job.error', (data: any) => {
              forward(data as JobUpdateEvent);
              settleReject(new Error((data && data.data && data.data.error) || 'Job failed'));
            });
            channel.bind('job.completed', (data: any) => {
              forward({ ...(data || {}), status: 'completed' } as JobUpdateEvent);
              settleResolve(data);
            });
          });

          channel.bind('pusher:subscription_error', (status: any) => {
            console.error('[Onvo] Pusher subscription failed with status:', status);
            settleReject(new Error(`Subscription error: ${status || 'unknown'}`));
          });

          pusher.connection.bind('error', (err: any) => {
            console.error('[Onvo] Pusher connection error:', err);
            settleReject(new Error(err?.message || 'WebSocket connection error'));
          });
        } catch (e: any) {
          reject(e);
        }
      });
    }
    else if (response.status === 200) {
      const data = await response.json().catch(() => ({}));
      return data?.data;
    }

    // Synchronous success path (rare for long jobs)
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error((data && data.message) || 'Request failed');
    }
    return data?.data;
  }

  /**
   * Fetches a Blob from the API.
   * @param url - The URL to fetch data from.
   * @returns The response from the API as a Blob.
   * @throws {Error} If there is an error making the request.
   */
  async _fetchBlob(url: string): Promise<Blob> {
    try {
      let headers: any = {
        "Content-Type": "application/json",
        "Access-Control-Allow-Origin": "*",
        "Access-Control-Allow-Methods": "GET,PUT,POST,DELETE,PATCH,OPTIONS",
      };
      if (this.#apiKey && this.#apiKey.trim() !== "") {
        headers["x-api-key"] = this.#apiKey;
      }
      const response = await fetch(this._endpoint + url, {
        method: "GET",
        headers: headers,
      }).then((response) => response.blob());
      return response;
    } catch (error: any) {
      throw new Error("Error in making the request: " + error.message);
    }
  }

  constructor(apiKey: string, options?: { endpoint: string }) {
    if (apiKey === undefined || apiKey === null)
      throw new Error("API key is required");
    this.#apiKey = apiKey;
    this._endpoint = options?.endpoint || "https://dashboard.onvo.ai";
  }
}
